import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../Common_Files/Config';
import axios from 'axios';

const generateUniqueFilename = (baseName, extension) => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
  return `${baseName}_${timestamp}.${extension}`;
};

const generateDeliveryNotePDF = async (id) => {
  const accessToken = localStorage.getItem('accessToken');
  const formData = new FormData();
  formData.set('sale_id', id);
  const apiUrl = `${config.api_url}/ws-get-sales-by-id`;

  try {
    const response = await axios.post(apiUrl, formData, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const data = await response.data;
    const totalSalesQty = data.product.reduce((acc, item) => acc + parseInt(item.salesQty, 10), 0);
    const totalPalletQty = data.product.reduce((acc, item) => acc + parseInt(item.pallet_qty, 10), 0);

    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const bottomBuffer = 28;
    let y = margin;

    const wrapText = (text, maxWidth) => doc.splitTextToSize(text || '', maxWidth);

    // Helper function to wrap text after specific character count
    const wrapTextByCharacters = (text, maxChars = 15) => {
      if (!text) return [''];
      const lines = [];
      for (let i = 0; i < text.length; i += maxChars) {
        lines.push(text.substring(i, i + maxChars));
      }
      return lines;
    };

    const loadImageAsDataUrl = async (src) => {
      try {
        const res = await fetch(src, { cache: 'no-store' });
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    const logoDataUrl = await loadImageAsDataUrl('/assets/img/logo.jpg');
    const logoWidth = 28;
    const logoHeight = 12;
    if (logoDataUrl) doc.addImage(logoDataUrl, 'JPEG', margin, y, logoWidth, logoHeight);

    const leftX = margin + logoWidth + 5;
    doc.setFontSize(12).setFont('helvetica', 'bold');
    doc.text('Star Pacific UK LTD', leftX, y + 4);

    const companyAddress = [
      'Unit 6 Abenglen Industrial Estate,',
      'Betam Road, Hayes',
      'Middlesex, London UB3 1AT',
      'United Kingdom',
    ];
    const smallLineHeight = 4;
    doc.setFontSize(8).setFont('helvetica', 'normal');
    companyAddress.forEach((line, idx) => {
      doc.text(line, leftX, y + 4 + smallLineHeight * (idx + 1));
    });

    const deliveryTitle = 'Delivery Address:';
    const deliveryLines = [
      data?.sale?.company_name || '',
      data?.sale?.address_line1 || '',
      data?.sale?.address_line2 || '',
      data?.sale?.city || '',
      data?.sale?.post_code || '',
      data?.sale?.country || '',
    ].filter(Boolean);

    let addrStartY = y + 4 + smallLineHeight * (companyAddress.length + 1);
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text(deliveryTitle, leftX - 25, addrStartY);
    const deliveryValueStartX = leftX - 25 + doc.getTextWidth(deliveryTitle + ' ');
    doc.setFontSize(8).setFont('helvetica', 'normal');

    let deliveryY = addrStartY;
    let totalDeliveryLines = 0;
    deliveryLines.forEach((line) => {
      const wrapped = wrapText(line, 60);
      wrapped.forEach((wl) => {
        doc.text(wl, deliveryValueStartX, deliveryY);
        deliveryY += smallLineHeight;
        totalDeliveryLines++;
      });
    });

    const rightX = pageWidth - margin;
    doc.setFontSize(16).setFont('helvetica', 'bold');
    doc.setTextColor(255, 99, 71);
    doc.text('Delivery Note', rightX, y - 1, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    const regInfoStartX = leftX + 50;
    doc.setFontSize(8).setFont('helvetica', 'bold');
    //doc.text('Co. Reg. No.: 7298396', regInfoStartX, y + 4);
    //doc.text('VAT Reg. No.: GB994533184', regInfoStartX, y + 4 + smallLineHeight);
    // Tel on first line (same Y as Co. Reg. above) now that two lines are commented out
    doc.text('Tel: 44-208 329 3800', regInfoStartX, y + 4);

    // Create delivery info with structured data for better control
    const deliveryInfoData = [
      { label: 'Customer PO', value: data?.sale?.customer_ref || '' },
      { label: 'Booking Ref', value: data?.sale?.booking_ref || '' },
      { 
        label: 'Booking Date', 
        value: data?.sale?.order_date || data?.sale?.created_at || '' 
      },
      { 
        label: 'Delivery Date', 
        value: data?.sale?.delivery_date !== '0000-00-00'
          ? data?.sale?.delivery_date +
            ' ' +
            (data?.sale?.delivery_time !== '00:00:00' ? data?.sale?.delivery_time : '00:00:00')
          : ''
      },
      { label: 'SO#', value: data?.sale?.reference_no || '' }
    ];

    let suppStartY = y + 4 + smallLineHeight * 4;
    doc.setFontSize(8).setFont('helvetica', 'normal');
    
    let currentY = suppStartY;
    
    deliveryInfoData.forEach((item) => {
      if (item.value) {
        if (item.label === 'Booking Ref') {
          const wrappedValue = wrapTextByCharacters(item.value, 15);
          doc.text(`${item.label}: ${wrappedValue[0]}`, rightX - 37, currentY, { align: 'left' });
          currentY += smallLineHeight;
          
          for (let i = 1; i < wrappedValue.length; i++) {
            doc.text(wrappedValue[i], rightX - 37, currentY, { align: 'left' });
            currentY += smallLineHeight;
          }
        } else {
          doc.text(`${item.label}: ${item.value}`, rightX - 37, currentY, { align: 'left' });
          currentY += smallLineHeight;
        }
      }
    });

    const totalDeliveryInfoHeight = (currentY - suppStartY);
    const deliveryLinesCount = totalDeliveryLines;
    y =
      Math.max(addrStartY + deliveryLinesCount * smallLineHeight, suppStartY + totalDeliveryInfoHeight) + 6;

    const columns = [
      'Line No',
      'SKU Code',
      'Product Description',
      'Pack',
      'Size',
      'Unit Barcode',
      'Case Qty',
      'Pallet Qty',
    ];
    const tableBody = data.product.map((item, index) => [
      index + 1,
      item.code,
      wrapText(item.product_desc, 50).join('\n'),
      item.pack,
      item.size,
      item.unit_barcode,
      item.salesQty,
      item.pallet_qty,
    ]);
    tableBody.push(['', '', '', '', '', 'Total:', totalSalesQty.toString(), totalPalletQty.toString()]);

    doc.autoTable({
      startY: y,
      head: [columns],
      body: tableBody,
      theme: 'grid',
      headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000' },
      bodyStyles: { fontSize: 7, lineHeight: 1.5, lineColor: '#000', lineWidth: 0.09 },
      margin: { top: 10 },
    });

    y = doc.autoTable.previous.finalY + 12;

    const palletData = [
      ['CHEP Standard', String(totalPalletQty)],
      ['LPR Standard', ''],
      ['IPP Standard', ''],
      ['CHEP Euro', ''],
      ['Others', ''],
    ];

    doc.autoTable({
      startY: y,
      body: palletData,
      theme: 'grid',
      headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000' },
      bodyStyles: { fontSize: 8, lineColor: '#000', lineWidth: 0.09 },
      margin: { top: 10 },
      tableWidth: 40,
      columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 10 } },
    });

    const palletEndY = doc.autoTable.previous.finalY;
    const rightStartX = margin + 50;
    const driverStartX = rightStartX;
    const customerStartX = rightStartX + 70;
    let rightY = y + 10;

    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text('Driver', driverStartX, rightY);
    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text('Loaded in good condition and correct pallet count', driverStartX, rightY + 5);
    doc.text('Name -------------------------------', driverStartX, rightY + 10);
    doc.text('Sign -------------------------------', driverStartX, rightY + 15);

    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text('Customer', customerStartX, rightY);
    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text('Received in good condition and correct pallet/case count', customerStartX, rightY + 5);
    doc.text('Name -------------------------------', customerStartX, rightY + 10);
    doc.text('Sign -------------------------------', customerStartX, rightY + 15);

    const combinedEndY = Math.max(palletEndY, rightY + 20);
    y = combinedEndY + 10;

    const footerColumns = [
      [
        { text: `Notes: ${data?.sale?.delivery_notes || data?.sale?.notes || ''}`, size: 8, bold: false },
        { text: `Ref: ${data?.sale?.job_ref || ''}`, size: 8, bold: false },
      ],
      [{ text: '', size: 8, bold: false }],
      [
        { text: `Gross Weight: ${data?.sale?.gross_weight || '0.00'} KGS`, size: 8, bold: false },
        { text: `Net Weight: ${data?.sale?.net_weight || '0.00'} KGS`, size: 8, bold: false },
      ],
    ];

    const measureColumnsHeight = (columnsArr) => {
      const adjustedWidth = (pageWidth - margin * 2) / columnsArr.length;
      let maxH = 0;
      columnsArr.forEach((col) => {
        let h = 0;
        col.forEach((item) => {
          const lines = wrapText(item.text, adjustedWidth - 2);
          h += lines.length * 4.5;
        });
        if (h > maxH) maxH = h;
      });
      return maxH;
    };

    const renderColumns = (columnsArr) => {
      const adjustedWidth = (pageWidth - margin * 2) / columnsArr.length;
      let maxColBottom = y;
      columnsArr.forEach((col, idx) => {
        const colStartX = margin + idx * adjustedWidth;
        let colY = y;
        col.forEach((item) => {
          const wrapped = wrapText(item.text, adjustedWidth - 2);
          doc.setFontSize(item.size - 0.5);
          doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
          wrapped.forEach((line) => {
            doc.text(line, colStartX, colY);
            colY += 4.5;
          });
        });
        if (colY > maxColBottom) maxColBottom = colY;
      });
      y = maxColBottom + 12;
    };

    const reservedBottom = bottomBuffer;
    const needed = measureColumnsHeight(footerColumns);
    if (y + needed > pageHeight - margin - reservedBottom) {
      doc.addPage();
      y = margin;
    }
    renderColumns(footerColumns);

    const footerSections3 = [
      [
        {
          text:
            'Subject to Star Pacific terms and conditions of sale, a copy of which is available on request',
          size: 8,
          bold: false,
        },
        { text: 'Star Pacific (No. 7298396)', size: 8, bold: false },
        {
          text:
            'Reservation of Title. The property in and the title to all goods sold by Star Pacific shall not pass to the buyer until all monies due and owing have been paid in full',
          size: 8,
          bold: false,
        },
      ],
    ];

    const bottomY = pageHeight - margin - footerSections3[0].length * 5;
    footerSections3.forEach((section) => {
      const centerX = pageWidth / 2;
      section.forEach((item, i) => {
        wrapText(item.text, pageWidth - 20).forEach((line, lineIdx) => {
          doc.setFontSize(item.size);
          doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
          doc.text(line, centerX, bottomY + (i + lineIdx) * 5, { align: 'center' });
        });
      });
    });

    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text('Page 1/1', pageWidth - margin, pageHeight - margin, { align: 'right' });

    const pdfBlob = doc.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    window.open(blobUrl, '_blank');
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};

export default generateDeliveryNotePDF;